import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Stack,
    IconButton,
    Typography,
} from "@mui/material";
import Dropdown from "../Input/Dropdown";
import { useEffect, useState } from "react";
import { possibleDateFormats } from "../../util/function/possibleDateFormats";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CircularProgress from "@mui/material/CircularProgress";

const getSampleData = (
    data: { [key: string]: string }[],
    column: string
): JSX.Element => {
    const sampleData = data.slice(0, 3).map((row) => row[column]);
    const allEmpty = sampleData.every((item) => !item);

    return <>{allEmpty ? "" : sampleData.join(", ")}</>;
};

type XlsxMapperProps = {
    data: { [key: string]: string }[];
    required: {
        key: string;
        label: string;
        type: "date" | "string" | "number";
        mandatory?: boolean;
    }[];
    defaultMapping: { [key: string]: string };
    defaultDateMapping: { [key: string]: string };
    submitMapping: (
        mapping: { [key: string]: string },
        dateMapping: { [key: string]: string }
    ) => void;
    back?: () => void;
    noStyle?: boolean;
};

const ColumnHeaderMap = (columnName: string) => {
    switch (columnName) {
        case "Dob text":
            return "Date of Birth";
        case "New dob text":
            return "New Date of Birth";
        case "Date of joining text":
            return "Date of joining";
        case "Date of resigning text":
            return "Date of Resigning";
        case "Sum insured":
            return "GMC Sum Insured";
        case "Plan":
            return "GMC Plan";
        default:
            return columnName;
    }
};

function XlsxMapper({
    data,
    required,
    defaultMapping,
    defaultDateMapping,
    submitMapping,
    back,
    noStyle = false,
}: XlsxMapperProps): JSX.Element {
    debugger
    const [excelColumns, setExcelColumns] =
        useState<{ value: string; available: boolean }[]>();
    const [mapping, setMapping] = useState<{
        [requiredLabel: string]: {
            excelColumn: string;
            dateFormat: string | null;
        };
    }>({});
    const [errorChecked, setErrorChecked] = useState<boolean>(false);
    const [uniqueId, setUniqueId] = useState<string>(
        Math.random().toString(36).substring(7)
    );
    const [loading, setLoading] = useState<boolean>(true);

    document.getElementById("scroller")?.scrollIntoView();

    useEffect(() => {
        let newExcelColumns = [];
        newExcelColumns = Object.keys(data[0]).map((d) => ({
            value: d,
            available: true,
        }));
        let newMapping: {
            [requiredLabel: string]: {
                excelColumn: string;
                dateFormat: string | null;
            };
        } = {};

        for (let i = 0; i < required.length; i++) {
            if (
                defaultMapping[required[i].key] &&
                data[0].hasOwnProperty(defaultMapping[required[i].key])
            ) {
                newMapping[required[i].label] = {
                    excelColumn: defaultMapping[required[i].key],
                    dateFormat: defaultDateMapping[required[i].key],
                };
                newExcelColumns = newExcelColumns?.map((col) => {
                    if (col.value === defaultMapping[required[i].key]) {
                        col.available = false;
                    }
                    return col;
                });
            }
        }
        setExcelColumns(newExcelColumns);
        setMapping(newMapping);
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }, [defaultMapping, defaultDateMapping]);

    const mapColumns = (
        requiredColumn: string,
        excelColumn: string | null,
        dateFormat: string | null = null
    ): void => {
        let newMapping = { ...mapping };
        let newExcelColumns = [...(excelColumns || [])];
        if (excelColumn) {
            newMapping[requiredColumn] = {
                excelColumn: excelColumn,
                dateFormat: dateFormat ? dateFormat : null,
            };
            newExcelColumns = newExcelColumns.map((col) => {
                if (col.value === mapping[requiredColumn]?.excelColumn) {
                    col.available = true;
                }
                if (col.value === excelColumn) {
                    col.available = false;
                }
                return col;
            });
        } else {
            newExcelColumns = newExcelColumns.map((col) => {
                if (col.value === newMapping[requiredColumn].excelColumn) {
                    col.available = true;
                }
                return col;
            });
            delete newMapping[requiredColumn];
        }
        setExcelColumns(newExcelColumns);
        setMapping(newMapping);
    };

    const hanldeSubmitMapping = (mapping: {
        [requiredLabel: string]: {
            excelColumn: string;
            dateFormat: string | null;
        };
    }): void => {
        setErrorChecked(true);
        if (!validateMapping()) {
            return;
        }
        let reversedMappping: {
            [key: string]: string;
        } = {};

        for (let i = 0; i < required.length; i++) {
            if (mapping[required[i].label]?.excelColumn) {
                reversedMappping[required[i].key] =
                    mapping[required[i].label]?.excelColumn;
            }
        }

        let newDateMapping: { [key: string]: string } = {};
        for (let i = 0; i < required.length; i++) {
            if (mapping[required[i].label]?.dateFormat) {
                newDateMapping[required[i].key] =
                    mapping[required[i].label].dateFormat || "";
            }
        }

        submitMapping(reversedMappping, newDateMapping);
    };

    const validateMapping = (): boolean => {
        for (let i = 0; i < required.length; i++) {
            if (
                required[i].mandatory &&
                !mapping[required[i].label]?.excelColumn
            ) {
                return false;
            }
            if (
                mapping[required[i].label]?.excelColumn &&
                required[i].type === "date" &&
                !mapping[required[i].label]?.dateFormat
            ) {
                return false;
            }
        }
        return true;
    };

    return (
        <>
            <span id="scroller"></span>
            {
                !noStyle && (

            <Stack
                direction="row"
                spacing={2}
                sx={{
                    marginBottom: "26px",
                }}
                alignItems={"center"}
            >
                {back && (
                    <IconButton onClick={() => back()}>
                        <KeyboardBackspaceIcon
                            sx={{
                                color: "#3E3C47",
                            }}
                        />
                    </IconButton>
                )}
                <Typography
                    variant="h4"
                    sx={{
                        color: "#111014",
                        fontSize: "24px !important",
                        fontStyle: "normal !important",
                        fontWeight: "600 !important",
                        lineHeight: "36px !important",
                        letterSpacing: "-0.48px !important",
                    }}
                >
                    Map your excel columns
                </Typography>
            </Stack>
                )
            }
            <Box
                sx={
                    noStyle ? {}:
                    {
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "32px",
                    border: "1px solid  rgba(145, 158, 171, 0.16)",
                }}
            >
                {loading ? (
                    <Stack alignItems={"center"} justifyContent={"center"}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: "16px",
                                overflowY: "hidden",
                            }}
                        >
                            <Table
                                sx={
                                    {
                                        // tableLayout: "fixed",
                                    }
                                }
                            >
                                <TableHead
                                    sx={{
                                        zIndex: 1000,
                                        position: "sticky",
                                        top: 0,
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            height: "47px",
                                            backgroundColor: "#f8f7fa",
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                borderBottom:
                                                    "1px solid rgba(0, 0, 0, 0.06)",
                                                fontSize: "12px !important",
                                                fontStyle: "normal !important",
                                                fontWeight: "600 !important",
                                            }}
                                        >
                                            Data
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                borderBottom:
                                                    "1px solid rgba(0, 0, 0, 0.06)",
                                                fontSize: "12px !important",
                                                fontStyle: "normal !important",
                                                fontWeight: "600 !important",
                                            }}
                                        >
                                            Excel Columns
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                borderBottom:
                                                    "1px solid rgba(0, 0, 0, 0.06)",
                                                fontSize: "12px !important",
                                                fontStyle: "normal !important",
                                                fontWeight: "600 !important",
                                            }}
                                        >
                                            Sample Column Values
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {required.map((req) => {
                                        return (
                                            <TableRow key={req.label}>
                                                <TableCell
                                                    sx={{
                                                        width: "20%",
                                                        borderBottom:
                                                            "1px solid rgba(0, 0, 0, 0.06)",
                                                        padding: "7px 12px",
                                                        fontSize:
                                                            "12px !important",
                                                        fontStyle:
                                                            "normal !important",
                                                        fontWeight:
                                                            "400 !important",
                                                    }}
                                                >
                                                    {ColumnHeaderMap(req.label)}{" "}
                                                    {req.mandatory && "*"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: "20%",
                                                        borderBottom:
                                                            "1px solid rgba(0, 0, 0, 0.06)",
                                                        padding: "7px 0px",
                                                        fontSize:
                                                            "12px !important",
                                                        fontStyle:
                                                            "normal !important",
                                                        fontWeight:
                                                            "400 !important",
                                                    }}
                                                >
                                                    <Stack spacing={1}>
                                                        <Dropdown
                                                            crossPos="-4px"
                                                            crossPosX="25px"
                                                            style={{
                                                                height: "24px",
                                                            }}
                                                            sx={{
                                                                "& fieldset": {
                                                                    border:
                                                                        errorChecked &&
                                                                        req.mandatory &&
                                                                        !mapping[
                                                                            req
                                                                                .label
                                                                        ]
                                                                            ?.excelColumn
                                                                            ? "solid 1px red"
                                                                            : "solid 1px rgba(145, 158, 171, 0.16)",
                                                                },
                                                                fontSize:
                                                                    "12px",
                                                                fontWeight:
                                                                    "400",
                                                            }}
                                                            backgroundColor="#F4F6F8"
                                                            defaultVal={
                                                                mapping[
                                                                    req.label
                                                                ]
                                                                    ?.excelColumn ||
                                                                ""
                                                            }
                                                            key={
                                                                req.label +
                                                                uniqueId
                                                            }
                                                            items={
                                                                excelColumns?.flatMap(
                                                                    (col) => {
                                                                        if (
                                                                            col.available ||
                                                                            col.value ===
                                                                                mapping[
                                                                                    req
                                                                                        .label
                                                                                ]
                                                                                    ?.excelColumn
                                                                        ) {
                                                                            return {
                                                                                id: col.value,
                                                                                name: col.value,
                                                                            };
                                                                        }
                                                                        return [];
                                                                    }
                                                                ) || []
                                                            }
                                                            change={(
                                                                val: string
                                                            ) => {
                                                                mapColumns(
                                                                    req.label,
                                                                    val
                                                                );
                                                            }}
                                                            size="small"
                                                            haveCancelCross={
                                                                true
                                                            }
                                                            onCancel={() =>
                                                                mapColumns(
                                                                    req.label,
                                                                    null
                                                                )
                                                            }
                                                        />

                                                        {req.type ===
                                                            "date" && (
                                                            <Dropdown
                                                                crossPos="-4px"
                                                                crossPosX="25px"
                                                                style={{
                                                                    height: "24px",
                                                                }}
                                                                sx={{
                                                                    "& fieldset":
                                                                        {
                                                                            border: errorChecked
                                                                                ? mapping[
                                                                                      req
                                                                                          .label
                                                                                  ]
                                                                                      ?.dateFormat
                                                                                    ? "none"
                                                                                    : mapping[
                                                                                          req
                                                                                              .label
                                                                                      ]
                                                                                          ?.excelColumn
                                                                                    ? `1px solid red`
                                                                                    : "solid 1px rgba(145, 158, 171, 0.16)"
                                                                                : "solid 1px rgba(145, 158, 171, 0.16)",
                                                                        },

                                                                    fontSize:
                                                                        "12px",
                                                                    fontWeight:
                                                                        "400",
                                                                }}
                                                                backgroundColor="#F4F6F8"
                                                                placeholder="Date Format"
                                                                defaultVal={
                                                                    mapping[
                                                                        req
                                                                            .label
                                                                    ]
                                                                        ?.dateFormat
                                                                }
                                                                items={possibleDateFormats(
                                                                    data.flatMap(
                                                                        (row) =>
                                                                            row[
                                                                                mapping[
                                                                                    req
                                                                                        .label
                                                                                ]
                                                                                    ?.excelColumn
                                                                            ] ||
                                                                            []
                                                                    )
                                                                ).map(
                                                                    (date) => {
                                                                        return {
                                                                            id: date,
                                                                            name: date,
                                                                        };
                                                                    }
                                                                )}
                                                                change={(
                                                                    val: string
                                                                ) => {
                                                                    mapColumns(
                                                                        req.label,
                                                                        mapping[
                                                                            req
                                                                                .label
                                                                        ]
                                                                            ?.excelColumn,
                                                                        val
                                                                    );
                                                                }}
                                                                size="small"
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        height: "1em",
                                                        width: "60%",
                                                        borderBottom:
                                                            "1px solid rgba(0, 0, 0, 0.06)",
                                                        padding: "7px 12px",
                                                        fontSize:
                                                            "12px !important",
                                                        fontStyle:
                                                            "normal !important",
                                                        fontWeight:
                                                            "400 !important",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            overflowX: "clip",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                            maxWidth: "100%",
                                                            "&:hover": {
                                                                overflow:
                                                                    "scroll",
                                                                textOverflow:
                                                                    "inherit",
                                                                marginTop:
                                                                    "1px",
                                                            },
                                                            "&::-webkit-scrollbar":
                                                                {
                                                                    height: "1px !important",
                                                                    width: "1px !important",
                                                                },
                                                        }}
                                                    >
                                                        {getSampleData(
                                                            data,
                                                            mapping[req.label]
                                                                ?.excelColumn
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                            sx={{
                                marginTop: "20px",
                            }}
                            spacing={2}
                        >
                            {
                                noStyle &&  back && (
                                    <Button
                                        onClick={() => back()}
                                        // className="btn simple"
                                        sx={{
                                            backgroundColor: "var(--white)",
                                            color: "#000",
                                            borderRadius: "1000px",
                                            width: "auto",
                                            padding: "12px 24px",
                                            textTransform: "none",
                                            "&:hover": {
                                                backgroundColor:
                                                    "var(--white)",
                                                filter: "brightness(95%)",
                                            },
                                            fontWeight: "600 !important",
                                        }}
                                    >
                                        Back
                                    </Button>
                                )
                            }

                            <Button
                                onClick={() => hanldeSubmitMapping(mapping)}
                                className="btn simple primary"
                                sx={{
                                    backgroundColor: "var(--primary)",
                                    color: "var(--white)",
                                    borderRadius: "1000px",
                                    width: "auto",
                                    padding: "12px 24px",
                                    textTransform: "none",
                                    "&:hover": {
                                        backgroundColor: "var(--primary)",
                                        filter: "brightness(95%)",
                                    },
                                    fontWeight: "600 !important",
                                }}
                            >
                                Next
                            </Button>
                        </Stack>
                    </>
                )}
            </Box>
        </>
    );
}

export default XlsxMapper;
